import { createApi } from '@reduxjs/toolkit/dist/query/react'
import baseQueryWithOnlineCourse from '../../app/baseQueryWithOnlineCourse'
import { MentorSalaryDetail, MentorSalaryList } from '../salary/salarySlice'

// Сервис басшысы (SERVICE_DIRECTOR): /api/director/*. Поля = director.proto, 0/"" -> null в bff.

export interface DirWeek {
    monthNumber: number
    weekNumber: number
    startDate: string | null
    endDate: string | null
    manual: boolean
}

export interface DirSubjectPeriod {
    courseSubjectId: number
    subjectId: number
    subjectTitleKz: string
    subjectTitleRu: string
    locked: boolean
    lockedAt: string | null
    lockedBy: number | null
    courseStart: string | null
    weeks: DirWeek[]
}

export interface DirPeriods {
    courseId: number
    courseStart: string | null
    subjects: DirSubjectPeriod[]
}

export interface DirCurrentPeriod {
    monthNumber: number | null
    weekNumber: number | null
    weekStart: string | null
    weekEnd: string | null
}

export interface DirSubjectRow {
    subjectId: number
    titleKz: string
    titleRu: string
    headIds: number[]
    headNames?: string[]
    mentorCount: number
    studentsTotal: number
    studentsActive: number
    studentsLeft: number
    studentsReturned: number
    videoPercent: number
    testPercent: number
    ratingPercent: number
}

export interface DirMentorRow {
    mentorId: number
    mentorName?: string
    courseId: number
    courseTitleKz: string
    courseTitleRu: string
    studentsTotal: number
    studentsActive: number
    studentsLeft: number
    studentsReturned: number
    videoPercent: number
    testPercent: number
}

export interface DirHeadSalary {
    headId: number
    headName?: string
    subjects?: { id: number; titleKz: string; titleRu: string }[]
    monthNumber: number
    base: number | null
    bonus: number | null
    payDate: string | null
    paid: boolean
    paidAt: string | null
    mentorCount?: number
    dueFrom?: string | null
    dueTo?: string | null
}

export interface DirReminderRow {
    subjectId: number
    subjectTitleKz: string
    subjectTitleRu: string
    groupId: number | null
    groupTitle: string
    unpaidMentors?: { id: number; name: string | null }[]
    amount: number
    approved: number
    total: number
}

export interface DirReminder {
    type: 'LATE' | 'DUE' | 'SOON'
    courseId: number
    courseTitleKz: string
    courseTitleRu: string
    monthNumber: number
    payFrom: string | null
    payTo: string | null
    workDays: number | null
    rows: DirReminderRow[]
}

export interface DirReminderList {
    reminders: DirReminder[]
    badge: number | null
}

export type SalaryDecision = 'APPROVE' | 'UNAPPROVE' | 'PAID' | 'UNPAID'

// bff отдаёт список либо голым массивом, либо обёрткой proto-ответа
const listOf = <T,>(data: any, key: string): T[] => (Array.isArray(data) ? data : data?.[key] ?? [])

// Одни и те же параметры для /director/subjects везде - один кэш, один запрос
export const dirSubjectsArgs = (f?: { courseId?: number | null; monthNumber?: number; weekNumber?: number }) => ({
    courseId: f?.courseId || 0,
    monthNumber: f?.monthNumber || 0,
    weekNumber: f?.weekNumber || 0,
})

const SALARY_TAGS = [
    { type: 'Salary' as const, id: 'LIST' },
    { type: 'Reminders' as const, id: 'ALL' },
]

export const directorApi = createApi({
    reducerPath: 'directorApi',
    baseQuery: baseQueryWithOnlineCourse,
    tagTypes: ['Periods', 'Current', 'Salary', 'Heads', 'Reminders'],
    endpoints: (build) => ({
        getPeriods: build.query<DirPeriods, { courseId: number }>({
            query: (params) => ({ url: 'director/periods', params }),
            providesTags: [{ type: 'Periods', id: 'ALL' }],
            transformResponse: (r: any) => r.data,
        }),
        approvePeriods: build.mutation<DirPeriods, { courseId: number; courseSubjectIds: number[]; courseStart: string; weeks: DirWeek[] }>({
            query: (body) => ({ url: 'director/periods/approve', method: 'POST', body }),
            invalidatesTags: ['Periods', 'Current', 'Reminders', 'Heads'],
            transformResponse: (r: any) => r.data,
        }),
        unlockPeriods: build.mutation<DirPeriods, { courseId: number; courseSubjectIds: number[] }>({
            query: (body) => ({ url: 'director/periods/unlock', method: 'POST', body }),
            invalidatesTags: ['Periods', 'Current', 'Reminders'],
            transformResponse: (r: any) => r.data,
        }),
        getCurrentPeriod: build.query<DirCurrentPeriod, { courseId: number; subjectId?: number; courseSubjectId?: number; groupId?: number }>({
            query: (params) => ({ url: 'director/current-period', params }),
            providesTags: [{ type: 'Current', id: 'ALL' }],
            transformResponse: (r: any) => r.data,
        }),
        getSubjects: build.query<DirSubjectRow[], { courseId?: number; monthNumber?: number; weekNumber?: number }>({
            query: (params) => ({ url: 'director/subjects', params }),
            transformResponse: (r: any) => listOf<DirSubjectRow>(r.data, 'subjects'),
        }),
        getSubjectMentors: build.query<DirMentorRow[], { subjectId: number; courseId?: number; monthNumber?: number; weekNumber?: number }>({
            query: ({ subjectId, ...params }) => ({ url: `director/subjects/${subjectId}/mentors`, params }),
            transformResponse: (r: any) => listOf<DirMentorRow>(r.data, 'mentors'),
        }),
        getSalary: build.query<MentorSalaryList, { courseId?: number; subjectId?: number; monthNumber: number; weekNumber?: number; filter?: string }>({
            query: (params) => ({ url: 'director/salary', params }),
            providesTags: [{ type: 'Salary', id: 'LIST' }],
            transformResponse: (r: any) => r.data,
        }),
        getSalaryDetail: build.query<MentorSalaryDetail, { mentorId: number; courseSubjectId: number; monthNumber: number; weekNumber?: number; groupId?: number }>({
            query: ({ mentorId, ...params }) => ({ url: `director/salary/${mentorId}`, params }),
            providesTags: [{ type: 'Salary', id: 'LIST' }],
            transformResponse: (r: any) => r.data,
        }),
        saveSurvey: build.mutation<any, { mentorUserId: number; courseSubjectId: number; cycleNumber: number; scorePercent: number }>({
            query: (body) => ({ url: 'director/salary/survey', method: 'PUT', body }),
            invalidatesTags: SALARY_TAGS,
        }),
        decide: build.mutation<any, { mentorUserId: number; courseSubjectId: number; cycleNumber: number; decision: SalaryDecision; comment?: string }>({
            query: ({ mentorUserId, ...body }) => ({ url: `director/salary/${mentorUserId}/decision`, method: 'POST', body }),
            invalidatesTags: SALARY_TAGS,
        }),
        getHeadSalaries: build.query<DirHeadSalary[], { monthNumber: number }>({
            query: (params) => ({ url: 'director/salary/heads', params }),
            providesTags: [{ type: 'Heads', id: 'ALL' }],
            transformResponse: (r: any) => listOf<DirHeadSalary>(r.data, 'heads'),
        }),
        saveHeadSalary: build.mutation<any, DirHeadSalary>({
            query: (body) => ({ url: 'director/salary/heads', method: 'PUT', body }),
            invalidatesTags: [{ type: 'Heads', id: 'ALL' }],
        }),
        getReminders: build.query<DirReminderList, { monthNumber?: number } | void>({
            query: (params) => ({ url: 'director/salary/reminders', params: params || {} }),
            providesTags: [{ type: 'Reminders', id: 'ALL' }],
            transformResponse: (r: any) => r.data,
        }),
    }),
})
